﻿import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { analyzeRunways, buildRunwayGhosts, resolveWindForRunway, runwayDisplayHeading, toTrueHeading, type WindSelection } from './analysis';
import { renderCompass } from './canvas/compass-renderer';
import { CrosswindContext, CrosswindCtx, buildHourlySlots, defaultSlotIndex, headingToLabel, primaryHeading, reciprocal } from './context';
import './crosswind-calculator.css';
import { calculateCrosswind } from './engine';
import { AirportSection, CompassSection, ControlsSection, ResultsSection, RunwaySection, RunwayTableSection } from './sections';
import type { CrosswindCalculatorProps, RunwayAnalysisRow, RunwayPair, WindData, WindSourceType } from './types';

// Keeps the last non-undefined value so a refetch that momentarily drops the prop
// does not blank the UI.
function useRetained<T>(value: T | undefined): T | undefined {
    const ref = useRef<T | undefined>(undefined);
    if (value !== undefined) {
        ref.current = value;
    }
    return value ?? ref.current;
}

function findEnd(runways: RunwayPair[] | undefined, id: string | undefined) {
    const pair = id ? runways?.find(p => p.base.id === id || p.recip.id === id) : undefined;
    return pair ? { pair, isRecip: pair.recip.id === id } : null;
}

const endHeading = (end: { pair: RunwayPair; isRecip: boolean }) =>
    end.isRecip ? end.pair.recip.heading : end.pair.base.heading;

function CrosswindCalculatorFn({
    defaultHeading = 0,
    defaultRunway,
    runways,
    windSources: windSourcesProp,
    defaultWindSource = 'metar',
    userDefinedWind: initialUserWind,
    gustFactor: initialGustFactor = 1.0,
    magneticVariation = 0,
    headingMode = 'magnetic',
    icao,
    iata,
    showValues = false,
    showGustAsDashed = false,
    showHeadingOnCompass = false,
    showMagVarIndicator = true,
    showRunwayTable = false,
    runwayRanking,
    showBestRunway = false,
    showRunwayBars = true,
    showRunwayUsage = true,
    compareRunways = 'none',
    scale = 1.05,
    theme: themeProp,
    className,
    children,
    onAirportSearch,
    airportSuggestions,
    onAirportSelect,
}: CrosswindCalculatorProps) {
    const airportKey = icao ?? iata;
    const runwayList = useRetained(runways);
    const windSources = useRetained(windSourcesProp);

    const defaultEnd = findEnd(runwayList, defaultRunway);
    const initialEnd = defaultEnd ?? (runwayList?.[0] ? { pair: runwayList[0], isRecip: false } : null);

    const [heading, setHeading] = useState<number>(initialEnd ? endHeading(initialEnd) : defaultHeading);
    const [flipped, setFlipped] = useState(!!initialEnd?.isRecip);
    const [selectedBaseId, setSelectedBaseId] = useState<string | null>(initialEnd?.pair.base.id ?? null);
    const [manualHeading, setManualHeading] = useState(false);
    const [selectedSource, setSelectedSource] = useState<WindSourceType>(defaultWindSource);
    const [selectedTafIndex, setSelectedTafIndex] = useState(0);
    const [selectedTafHourIndex, setSelectedTafHourIndex] = useState(0);
    const [gustFactor, setGustFactor] = useState(initialGustFactor);
    const [userWind, setUserWind] = useState<WindData>(initialUserWind ?? { direction: 0, speed: 0 });
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [itwsSubSource, setItwsSubSource] = useState<'runway' | 'centerfield'>('runway');
    const [itwsRunwayId, setItwsRunwayId] = useState<string | null>(null);
    const [itwsFlightType, setItwsFlightType] = useState<'arrival' | 'departure'>('arrival');
    const [hoveredRunwayId, setHoveredRunwayId] = useState<string | null>(null);

    const selectedRunwayPair = useMemo(
        () => runwayList?.find(p => p.base.id === selectedBaseId) ?? null,
        [runwayList, selectedBaseId]
    );
    const setSelectedRunwayPair = useCallback((pair: RunwayPair | null) => {
        setSelectedBaseId(pair?.base.id ?? null);
        setManualHeading(!pair);
    }, []);

    const appliedRunway = useRef<string | undefined>(defaultEnd ? `${airportKey}|${defaultRunway}` : undefined);
    useEffect(() => {
        const key = `${airportKey}|${defaultRunway}`;
        if (!defaultRunway || appliedRunway.current === key) {
            return;
        }
        const end = findEnd(runwayList, defaultRunway);
        if (!end) {
            return;
        }
        appliedRunway.current = key;
        setSelectedBaseId(end.pair.base.id);
        setHeading(endHeading(end));
        setManualHeading(false);
    }, [defaultRunway, runwayList, airportKey]);

    useEffect(() => {
        const first = runwayList?.[0];
        if (manualHeading || !first) {
            return;
        }
        if (selectedBaseId && runwayList!.some(p => p.base.id === selectedBaseId)) {
            return;
        }
        if (findEnd(runwayList, defaultRunway)) {
            return;
        }
        setSelectedBaseId(first.base.id);
        setHeading(first.base.heading);
    }, [runwayList, selectedBaseId, manualHeading, defaultRunway]);

    const appliedHeading = useRef(defaultHeading);
    useEffect(() => {
        if (appliedHeading.current === defaultHeading) {
            return;
        }
        appliedHeading.current = defaultHeading;
        if (!defaultRunway) {
            setSelectedBaseId(null);
            setManualHeading(true);
            setHeading(defaultHeading);
        }
    }, [defaultHeading, defaultRunway]);

    const labelHeading = headingMode === 'magnetic' ? heading : ((heading - magneticVariation) % 360 + 360) % 360;
    const primaryLabel = headingToLabel(primaryHeading(labelHeading));
    const reciprocalLabel = headingToLabel(reciprocal(primaryHeading(labelHeading)));

    useEffect(() => {
        setFlipped(heading > 184 || heading <= 4);
    }, [heading]);

    const theme =
        themeProp ?? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

    const availableSources = useMemo(() => {
        const s: WindSourceType[] = [];
        if (windSources?.metar) {
            s.push('metar');
        }
        if (windSources?.datis) {
            s.push('datis');
        }
        if (windSources?.itws && (windSources.itws.runways?.length || windSources.itws.centerfield)) {
            s.push('itws');
        }
        if (windSources?.taf?.periods.length) {
            s.push('taf');
        }
        s.push('user');
        return s;
    }, [windSources]);

    // While data is loading only 'user' exists, so the selection must keep tracking
    // `defaultWindSource` until the user picks a source themselves.
    const sourcePicked = useRef(false);
    const pickSource = useCallback((source: WindSourceType) => {
        sourcePicked.current = true;
        setSelectedSource(source);
    }, []);

    useEffect(() => {
        const target = availableSources.includes(defaultWindSource) ? defaultWindSource : availableSources[0]!;
        if (sourcePicked.current) {
            if (!availableSources.includes(selectedSource)) {
                setSelectedSource(target);
            }
            return;
        }
        if (selectedSource !== target) {
            setSelectedSource(target);
        }
    }, [availableSources, selectedSource, defaultWindSource]);

    useEffect(() => {
        if (windSources && selectedSource === 'itws') {
            const itws = windSources.itws;
            if (!itws?.runways?.length && itws?.centerfield) { setItwsSubSource('centerfield'); }
        }
    }, [windSources, selectedSource]);

    const itwsRunwayOptions = useMemo(
        () => windSources?.itws?.runways?.map(r => r.runwayId) ?? [],
        [windSources]
    );

    const itwsEffectiveRunwayId = useMemo(() => {
        const rwyList = windSources?.itws?.runways;
        if (!rwyList?.length) { return null; }
        if (itwsRunwayId && rwyList.some(r => r.runwayId === itwsRunwayId)) { return itwsRunwayId; }
        const activeEnd = selectedRunwayPair
            ? (flipped ? selectedRunwayPair.recip.id : selectedRunwayPair.base.id)
            : null;
        if (activeEnd && rwyList.some(r => r.runwayId === activeEnd)) { return activeEnd; }
        return rwyList[0]!.runwayId;
    }, [itwsRunwayId, selectedRunwayPair, flipped, windSources]);

    const tafHourlySlots = useMemo(() => (windSources?.taf?.periods ? buildHourlySlots(windSources.taf.periods) : null), [windSources]);

    useEffect(() => {
        if (tafHourlySlots) {
            setSelectedTafHourIndex(defaultSlotIndex(tafHourlySlots));
        }
    }, [tafHourlySlots]);

    const selection = useMemo<WindSelection>(() => ({
        source: selectedSource,
        itwsSubSource,
        itwsFlightType,
        tafPeriodIndex: selectedTafIndex,
        tafHourIndex: selectedTafHourIndex,
        tafHourlySlots,
        userWind
    }), [selectedSource, itwsSubSource, itwsFlightType, selectedTafIndex, selectedTafHourIndex, tafHourlySlots, userWind]);

    const activeWind: WindData = useMemo(
        () => resolveWindForRunway(itwsEffectiveRunwayId, windSources, selection),
        [itwsEffectiveRunwayId, windSources, selection]
    );

    const runwayRows = useMemo(
        () => analyzeRunways(runwayList, { windSources, selection, gustFactor, magneticVariation, headingMode, ranking: runwayRanking }),
        [runwayList, windSources, selection, gustFactor, magneticVariation, headingMode, runwayRanking]
    );

    const hoveredRow = useMemo(
        () => runwayRows.find(r => r.id === hoveredRunwayId) ?? null,
        [runwayRows, hoveredRunwayId]
    );

    // The selected pair is always in the ghost set so both of its ends stay clickable on the
    // compass (the renderer skips drawing it); the hovered runway is added so it is always outlined.
    const alwaysGhostIds = useMemo(() => {
        const ids: string[] = [];
        if (selectedRunwayPair) {
            ids.push(selectedRunwayPair.base.id);
        }
        if (hoveredRow) {
            ids.push(hoveredRow.id);
        }
        return ids;
    }, [selectedRunwayPair, hoveredRow]);

    const runwayGhosts = useMemo(
        () => buildRunwayGhosts(runwayRows, compareRunways, alwaysGhostIds),
        [runwayRows, compareRunways, alwaysGhostIds]
    );

    const selectRunway = useCallback((row: RunwayAnalysisRow) => {
        setSelectedRunwayPair(row.pair);
        setFlipped(row.isRecip);
        setHeading(row.isRecip ? row.pair.recip.heading : row.pair.base.heading);
        setItwsRunwayId(null);
    }, []);

    const engineHeading = toTrueHeading(heading, magneticVariation, headingMode);
    const result = useMemo(
        () => calculateCrosswind(engineHeading, activeWind, gustFactor),
        [engineHeading, activeWind, gustFactor]
    );

    const drawCompass = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        // While hovering a runway, the components/arrows preview that runway instead of the selected one.
        const compWind = hoveredRow ? hoveredRow.wind : activeWind;
        const compResult = hoveredRow ? hoveredRow.result : result;
        const renderWindDir =
            headingMode === 'magnetic' ? (((compWind.direction - magneticVariation) % 360) + 360) % 360 : compWind.direction;
        const renderMagVar = headingMode === 'magnetic' ? 0 : magneticVariation;
        const activeRunwayId = selectedRunwayPair ? (flipped ? selectedRunwayPair.recip.id : selectedRunwayPair.base.id) : undefined;
        const recipRunwayId = selectedRunwayPair ? (flipped ? selectedRunwayPair.base.id : selectedRunwayPair.recip.id) : undefined;
        renderCompass(canvas, {
            runwayHeading: heading,
            componentHeading: hoveredRow ? runwayDisplayHeading(hoveredRow) : heading,
            windDirection: renderWindDir,
            windSpeed: compWind.speed,
            gustSpeed: compWind.gust,
            headwind: compResult.headwind,
            crosswind: compResult.crosswind,
            magneticVariation: renderMagVar,
            windArrowScaled: true,
            showValues,
            showGustAsDashed,
            showHeadingOnCompass,
            theme,
            activeRunwayId,
            recipRunwayId,
            ghosts: runwayGhosts.map(g => ({
                bearing: g.bearing,
                label: g.label,
                recipLabel: g.recipLabel,
                isBest: showBestRunway && g.isBest,
                isHovered:
                    hoveredRunwayId != null &&
                    (g.ends.some(r => r.id === hoveredRunwayId) || g.recipEnds.some(r => r.id === hoveredRunwayId))
            }))
        });
    }, [
        heading,
        activeWind,
        result,
        theme,
        showHeadingOnCompass,
        showValues,
        showGustAsDashed,
        flipped,
        headingMode,
        magneticVariation,
        selectedRunwayPair,
        runwayGhosts,
        hoveredRunwayId,
        hoveredRow,
        showBestRunway
    ]);

    useEffect(() => {
        drawCompass();
    }, [drawCompass]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        const observer = new ResizeObserver(drawCompass);
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [drawCompass]);

    const ctx = {
        heading,
        setHeading,
        flipped,
        setFlipped,
        primaryLabel,
        reciprocalLabel,
        magneticVariation,
        headingMode,
        icao,
        iata,
        canvasRef,
        showMagVarIndicator,
        scale,
        runways: runwayList,
        selectedRunwayPair,
        setSelectedRunwayPair,
        selectedSource,
        setSelectedSource: pickSource,
        selectedTafIndex,
        setSelectedTafIndex,
        selectedTafHourIndex,
        setSelectedTafHourIndex,
        gustFactor,
        setGustFactor,
        userWind,
        setUserWind,
        activeWind,
        result,
        runwayRows,
        runwayGhosts,
        runwayRanking,
        showBestRunway,
        showRunwayBars,
        showRunwayUsage,
        hoveredRunwayId,
        setHoveredRunwayId,
        selectRunway,
        tafHourlySlots,
        windSources,
        availableSources,
        itwsSubSource,
        setItwsSubSource,
        itwsRunwayOptions,
        itwsRunwayId,
        setItwsRunwayId,
        itwsEffectiveRunwayId,
        itwsFlightType,
        setItwsFlightType,
        onAirportSearch,
        airportSuggestions,
        onAirportSelect,
    } satisfies CrosswindCtx;

    return (
        <CrosswindContext.Provider value={ctx}>
            <div
                className={`fe-crosswind ${theme === 'dark' ? 'fe-crosswind--dark' : ''} ${className ?? ''}`}
                style={{ '--cw-compass-size': `${300 * scale}px` } as CSSProperties}>
                {children ?? (
                    <div className="fe-crosswind__row">
                        {showRunwayTable && runwayList && runwayList.length > 0 && <RunwayTableSection />}
                        <div className="fe-crosswind__compass-col">
                            {(icao || iata || magneticVariation !== 0 || onAirportSearch || onAirportSelect) && <AirportSection />}
                            <CompassSection />
                        </div>
                        <div className="fe-crosswind__controls">
                            <RunwaySection />
                            <ControlsSection />
                            <ResultsSection />
                        </div>
                    </div>
                )}
            </div>
        </CrosswindContext.Provider>
    );
}

export const CrosswindCalculator = Object.assign(CrosswindCalculatorFn, {
    Compass: CompassSection,
    Airport: AirportSection,
    Runway: RunwaySection,
    Controls: ControlsSection,
    Results: ResultsSection,
    RunwayTable: RunwayTableSection
});
